import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { facilitiesAPI, sportsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, Plus, MapPin, Search, Wifi, Car, ShowerHead, Coffee, CheckCircle, Map as MapIcon, List, X, ArrowRight } from 'lucide-react';
import { resolveMediaUrl } from '@/lib/utils';
import { FacilitiesMap } from '@/components/FacilityMap';

const AMENITY_ICONS = {
  bar: { icon: Coffee, label: 'Bar' },
  showers: { icon: ShowerHead, label: 'Docce' },
  parking: { icon: Car, label: 'Parcheggio' },
  wifi: { icon: Wifi, label: 'Wi-Fi' },
};

export const FacilitiesPage = () => {
  const { user } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [view, setView] = useState('map'); // phones only: desktop shows both
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [facRes, sportsRes] = await Promise.all([
          facilitiesAPI.getAll(),
          sportsAPI.getAll(),
        ]);
        setFacilities(facRes.data);
        setSports(sportsRes.data);
      } catch (error) {
        console.error('Error fetching facilities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchFiltered = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (cityFilter) params.city = cityFilter;
      if (sportFilter && sportFilter !== 'all') params.sport_id = sportFilter;
      const res = await facilitiesAPI.getAll(params);
      setFacilities(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFiltered();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, cityFilter, sportFilter]);

  const getSportName = (sportId) => {
    const sport = sports.find(s => s.sport_id === sportId);
    return sport?.name || sportId;
  };

  const filtered = Boolean(search || cityFilter || (sportFilter && sportFilter !== 'all'));
  const selected = facilities.find(f => f.facility_id === selectedId);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="facilities-page">
      {/* Marketplace hero */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 p-4 md:p-6 text-white mb-3 md:mb-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div>
            <div className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 bg-white/20 rounded-full text-xs font-semibold mb-2">
              <Building2 className="h-3 w-3" />
              Marketplace
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">Strutture sportive</h1>
            <p className="hidden md:block opacity-90 mt-1 text-base">
              Trova un campo sulla mappa e prenota l'orario libero
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="secondary" size="sm" className="md:h-10 md:px-4 bg-white text-emerald-700 hover:bg-white/95 font-semibold">
              <Link to="/business">
                <Building2 className="h-4 w-4 mr-2" />
                Sei una struttura? Iscriviti
              </Link>
            </Button>
            {user?.role === 'admin' && (
              <Button asChild size="sm" className="md:h-10 md:px-4 bg-white/20 hover:bg-white/30 border border-white/30" data-testid="create-facility-btn">
                <Link to="/facilities/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Registra
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 mb-3 lg:mb-4">
        <div className="relative col-span-2 md:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca struttura..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="facility-search"
          />
        </div>
        <Input
          placeholder="Filtra per città..."
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          data-testid="facility-city-filter"
        />
        <Select value={sportFilter || 'all'} onValueChange={setSportFilter}>
          <SelectTrigger data-testid="facility-sport-filter">
            <SelectValue placeholder="Tutti gli sport" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli sport</SelectItem>
            {sports.map(sport => (
              <SelectItem key={sport.sport_id} value={sport.sport_id}>
                {sport.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="lg:hidden grid grid-cols-2 gap-1 p-1 mb-3 rounded-xl bg-muted" data-testid="facility-view-toggle">
        {[['map', MapIcon, 'Mappa'], ['list', List, 'Lista']].map(([key, Icon, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition ${
              view === key ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <div className={view === 'list' ? '' : 'hidden lg:block'}>
          {/* Results */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse flex overflow-hidden">
                  <div className="w-28 h-28 bg-muted shrink-0" />
                  <CardContent className="p-4 space-y-2 flex-1">
                    <div className="h-5 bg-muted rounded w-2/3" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : facilities.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nessuna struttura trovata</h3>
                <p className="text-muted-foreground mb-4">
                  {search || cityFilter || sportFilter
                    ? 'Prova a modificare i filtri di ricerca'
                    : 'Registra la tua struttura sportiva per iniziare'}
                </p>
                {user?.role === 'admin' && (
                  <Button asChild>
                    <Link to="/facilities/new">
                      <Plus className="h-4 w-4 mr-2" />
                      Registra Struttura
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {facilities.map(facility => (
                <Link
                  key={facility.facility_id}
                  to={`/facilities/${facility.facility_id}`}
                  onMouseEnter={() => setSelectedId(facility.facility_id)}
                  className="block"
                >
                  <Card className={`flex overflow-hidden hover:shadow-md transition-all ${
                    selectedId === facility.facility_id ? 'ring-2 ring-emerald-500' : ''
                  }`}>
                    <div className="w-28 sm:w-36 shrink-0 bg-gradient-to-br from-emerald-400/20 to-blue-500/20 relative">
                      {facility.cover_image_url ? (
                        <img
                          src={resolveMediaUrl(facility.cover_image_url)}
                          alt={facility.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full min-h-28">
                          <Building2 className="h-10 w-10 text-emerald-600/40" />
                        </div>
                      )}
                      {facility.tier === 'premium' && (
                        <Badge className="absolute top-1.5 left-1.5 bg-gradient-to-br from-purple-600 to-indigo-600 text-white border-0 text-[10px] px-1.5">
                          ⭐ Premium
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-3 sm:p-4 min-w-0 flex-1">
                      <div className="flex items-start gap-1.5">
                        <h3 className="font-semibold leading-tight line-clamp-2 flex-1">{facility.name}</h3>
                        {facility.is_verified && <CheckCircle className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />}
                      </div>
                      {(facility.city || facility.address) && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{facility.city || facility.address}</span>
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(facility.sport_ids || []).slice(0, 3).map(sid => (
                          <Badge key={sid} variant="secondary" className="text-xs">
                            {getSportName(sid)}
                          </Badge>
                        ))}
                        {(facility.sport_ids || []).length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{facility.sport_ids.length - 3}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">
                          {facility.fields_count || 0} camp{facility.fields_count === 1 ? 'o' : 'i'}
                        </span>
                        {Object.entries(facility.amenities || {}).filter(([, v]) => v).slice(0, 4).map(([key]) => {
                          const amenity = AMENITY_ICONS[key];
                          if (!amenity) return null;
                          const Icon = amenity.icon;
                          return <Icon key={key} className="h-3.5 w-3.5 text-muted-foreground" title={amenity.label} />;
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div className={view === 'map' ? '' : 'hidden lg:block'}>
          <div className="relative h-[62vh] lg:h-[calc(100vh-9rem)] lg:sticky lg:top-4 rounded-2xl overflow-hidden border shadow-sm">
            <FacilitiesMap
              facilities={facilities}
              selectedId={selectedId}
              onSelect={setSelectedId}
              fitToPins={filtered}
              className="h-full w-full"
            />
            {selected && (
              <div className="absolute bottom-3 left-3 right-3 z-[1000]" data-testid="map-selected-facility">
                <Card className="flex overflow-hidden shadow-xl">
                  <div className="w-24 shrink-0 bg-gradient-to-br from-emerald-400/20 to-blue-500/20 relative">
                    {selected.cover_image_url ? (
                      <img
                        src={resolveMediaUrl(selected.cover_image_url)}
                        alt={selected.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Building2 className="h-8 w-8 text-emerald-600/40" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3 min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold leading-tight truncate">{selected.name}</h3>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {selected.city || selected.address}
                          {' · '}
                          {selected.fields_count || 0} camp{selected.fields_count === 1 ? 'o' : 'i'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedId(null)}
                        className="p-1 -m-1 text-muted-foreground hover:text-foreground"
                        aria-label="Chiudi"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <Button asChild size="sm" className="mt-2 w-full bg-gradient-to-br from-emerald-500 to-blue-600 hover:opacity-90">
                      <Link to={`/facilities/${selected.facility_id}`}>
                        Vedi campi e prenota
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitiesPage;
